import { TPPAdapters } from '@seapoint/server/enums';
import axios from 'axios';
import { useState } from 'react';
import { TellMoneyConsent } from '../../../server/src/adapters/tpp/tell-money/types/consent.types';

export const useTppApi = ({ adapter }: { adapter?: TPPAdapters } = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserConsents = async (): Promise<TellMoneyConsent[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/tpp/consents');

      return response.data;
    } catch (err) {
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.message || err.message
        : 'An unknown error occurred';
      setError(errorMessage);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getConsent = async (consentId: string): Promise<TellMoneyConsent | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/tpp/consent/${consentId}`, {
        params: { adapter },
      });

      return response.data;
    } catch (err) {
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.message || err.message
        : 'An unknown error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const submitConsentDecision = async (
    consentId: string,
    approved: boolean,
    accountIds?: string[],
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/tpp/consent/decision`,
        {
          consentId,
          outcome: approved ? 'Pass' : 'Fail',
          accounts: accountIds,
        },
        {
          params: { adapter },
          withCredentials: true,
        },
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }

      return true;
    } catch (err) {
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.message || err.message
        : 'An unknown error occurred';
      setError(errorMessage);
      return false;
    } finally {
      // Only set isLoading to false if we're not redirecting
      if (!window.location.href.includes(window.location.origin)) {
        setIsLoading(false);
      }
    }
  };

  const deleteConsent = async (consentId: string, adapterType: TPPAdapters): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await axios.delete(`/api/tpp/consent/${consentId}`, {
        params: { adapter: adapterType },
      });
      return true;
    } catch (err) {
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.message || err.message
        : 'An unknown error occurred';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getUserConsents,
    getConsent,
    submitConsentDecision,
    deleteConsent,
    isLoading,
    error,
  };
};
